import { useEffect, useState } from 'react';
import { Box, Typography, Skeleton, Alert, Button } from '@mui/material';
import { ShoppingBag } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import purchaseService from '../../purchases/services/purchaseService';
import { Purchase } from '../../purchases/types/purchase.types';
import { PurchaseCard } from '../../purchases/components/PurchaseCard';

export function MyRecentPurchases() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [purchases, setPurchases] = useState<Purchase[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadPurchases = async () => {
      try {
        setLoading(true);
        const result = await purchaseService.getMyPurchases({ page: 1, limit: 3 });
        setPurchases(result.data);
        setTotal(result.meta.total);
        setError(null);
      } catch (err) {
        console.error('Failed to load purchases:', err);
        setError(t('home.purchases.errorLoading'));
      } finally {
        setLoading(false);
      }
    };

    loadPurchases();
  }, [t]);

  const SectionHeader = ({ showViewAll }: { showViewAll: boolean }) => (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        mb: 2,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: '16px',
            bgcolor: 'primary.main',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <ShoppingBag sx={{ color: 'white', fontSize: 24 }} />
        </Box>
        <Typography variant="h6" fontWeight={700} data-testid="my-recent-purchases-title">
          {t('home.purchases.title')}
        </Typography>
      </Box>
      {showViewAll && (
        <Button
          variant="text"
          size="small"
          onClick={() => navigate('/purchases')}
          data-testid="recent-purchases-view-all"
          sx={{ fontWeight: 500, textTransform: 'none' }}
        >
          {t('home.purchases.viewAll')}
        </Button>
      )}
    </Box>
  );

  if (loading) {
    return (
      <Box data-testid="recent-purchases-loading">
        <SectionHeader showViewAll={false} />
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {[1, 2, 3].map(i => (
            <Box
              key={i}
              sx={{
                display: 'flex',
                gap: 2,
                p: 2,
                borderRadius: 2,
                bgcolor: 'background.paper',
                boxShadow: 1,
              }}
            >
              <Skeleton
                variant="rectangular"
                width={90}
                height={90}
                sx={{ borderRadius: 2, flexShrink: 0 }}
              />
              <Box sx={{ flex: 1 }}>
                <Skeleton variant="text" width="70%" height={24} />
                <Skeleton variant="text" width="50%" height={20} />
                <Skeleton variant="text" width="60%" height={20} />
                <Skeleton
                  variant="rectangular"
                  width={60}
                  height={24}
                  sx={{ borderRadius: 4, mt: 0.5 }}
                />
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <SectionHeader showViewAll={false} />
        <Alert severity="error" data-testid="recent-purchases-error">
          {error}
        </Alert>
      </Box>
    );
  }

  if (purchases.length === 0) {
    return (
      <Box>
        <SectionHeader showViewAll={false} />
        <Typography variant="body2" color="text.secondary" data-testid="recent-purchases-empty">
          {t('home.purchases.noPurchases')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <SectionHeader showViewAll={total > 3} />
      <Box
        sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}
        data-testid="my-recent-purchases-list"
      >
        {purchases.map(purchase => (
          <PurchaseCard key={purchase.id} purchase={purchase} />
        ))}
      </Box>
    </Box>
  );
}
